import type { WorkOrder } from "./types";

/**
 * Normalises a server-side `Ticket` payload to the WorkOrder domain shape.
 *
 * The server schema still names the type `Ticket`, so most fields pass
 * through unchanged. A handful are renamed at the boundary so the rest of
 * the WorkOrderDetails feature never has to say `ticket`:
 *
 *   ticket.ticketId        -> workOrderNumber (the human readable WO #)
 *   ticket.ticketType      -> workOrderType
 *   ticket.machine         -> asset
 *   ticket.facility        -> company
 *   ticket.linkedTickets   -> linkedWorkOrders
 */
export const toWorkOrder = (
  ticket: Record<string, unknown> | null | undefined,
): WorkOrder | null => {
  if (!ticket) return null;
  const t = ticket as Record<string, any>;
  return {
    _id: t._id,
    workOrderNumber: t.ticketId,
    title: t.title,
    description: t.description,
    solution: t.solution,
    status: t.status,
    archived: t.archived,
    createdAt: t.createdAt,
    dueDate: t.dueDate,
    hasOriginalEventBeenDeleted: t.hasOriginalEventBeenDeleted,
    totalTimeLoggedInSeconds: t.totalTimeLoggedInSeconds,
    workOrderType: t.ticketType,
    schedule: t.schedule,
    assets: t.assets,
    company: t.facility,
    unaffiliatedAssetLinkedTo: t.unaffiliatedAssetLinkedTo,
    companyContacts: t.companyContacts,
    createdBy: t.createdBy,
    createdByContact: t.createdByContact,
    assignees: t.assignees,
    followers: t.followers,
    teams: t.teams,
    resources: t.resources,
    inventoryParts: t.inventoryParts?.length
      ? t.inventoryParts?.filter((part) => part.isAccessibleByTeam)
      : [],
    attachments: t.attachments,
    aiNotes: t.aiNotes ?? [],
    parentWorkOrder: t.parentWorkOrder
      ? {
          _id: t.parentWorkOrder._id,
          title: t.parentWorkOrder.title,
          workOrderNumber: t.parentWorkOrder.ticketId,
          workOrderType: t.parentWorkOrder.ticketType,
          assets: t.parentWorkOrder.assets ?? [],
          company: t.parentWorkOrder.facility ?? null,
        }
      : t.parentWorkOrder,
    linkedWorkOrders: t.linkedTickets?.map((link: any) => {
      const raw = link.ticket;
      return {
        ...link,
        ticket: undefined,
        workOrder: raw
          ? {
              _id: raw._id,
              workOrderNumber: raw.ticketId,
              title: raw.title,
              status: raw.status,
              workOrderType: raw.ticketType,
              company: raw.facility,
              assets: raw.assets,
              teams: raw.teams,
            }
          : null,
      };
    }),
    linkedSupportTickets: t.linkedSupportTickets?.map((link: any) => ({
      supportTicket: link.supportTicket
        ? {
            _id: link.supportTicket._id,
            title: link.supportTicket.title,
            supportTicketId: link.supportTicket.supportTicketId,
            type: link.supportTicket.type,
          }
        : null,
      linkedBy: link.linkedBy,
      linkedAt: link.linkedAt,
    })),
    subWorkOrders: t.subWorkOrders?.map((sub: any) => ({
      _id: sub._id,
      workOrderNumber: sub.ticketId,
      title: sub.title,
      status: sub.status,
      workOrderType: sub.ticketType,
      company: sub.facility,
      assets: sub.assets,
      teams: sub.teams,
    })),
    procedures: t.procedures,
    customFields: t.customFields,
  };
};
